import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react"
import { bundledContent, loadLiveContent } from "./live"
import {
  isPreviewMode,
  PREVIEW_MESSAGE,
  PREVIEW_READY,
  readPreviewDraft,
  writePreviewDraft,
  type PreviewDraft,
} from "./preview"

const ContentContext = createContext<PreviewDraft>(bundledContent)

function normalizeDraft(draft: PreviewDraft): PreviewDraft {
  return {
    ...bundledContent,
    ...draft,
    about: draft.about ?? bundledContent.about,
    work: draft.work ?? bundledContent.work,
    stories: draft.stories ?? bundledContent.stories,
    homepage: draft.homepage ?? bundledContent.homepage,
    site: draft.site ?? bundledContent.site,
  }
}

export function PreviewProvider({ children }: PropsWithChildren) {
  const [live, setLive] = useState<PreviewDraft>(bundledContent)
  const [draft, setDraft] = useState<PreviewDraft | undefined>(() => {
    if (!isPreviewMode()) return undefined
    const stored = readPreviewDraft()
    return stored ? normalizeDraft(stored) : undefined
  })

  useEffect(() => {
    if (isPreviewMode()) return
    let cancelled = false

    const reload = () => {
      void loadLiveContent().then((next) => {
        if (!cancelled) setLive(next)
      })
    }

    reload()

    const onVisible = () => {
      if (document.visibilityState === "visible") reload()
    }
    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener("focus", reload)
    return () => {
      cancelled = true
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener("focus", reload)
    }
  }, [])

  useEffect(() => {
    if (!isPreviewMode()) return

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== PREVIEW_MESSAGE || !event.data.draft) return
      const next = normalizeDraft(event.data.draft as PreviewDraft)
      writePreviewDraft(next)
      setDraft(next)
    }

    window.addEventListener("message", onMessage)
    window.parent.postMessage({ type: PREVIEW_READY }, window.location.origin)
    return () => window.removeEventListener("message", onMessage)
  }, [])

  return (
    <ContentContext.Provider value={draft ?? live}>
      {children}
    </ContentContext.Provider>
  )
}

export function useSiteConfig() {
  return useContext(ContentContext).site
}

export function useHomepageConfig() {
  return useContext(ContentContext).homepage
}

export function useStoriesConfig() {
  return useContext(ContentContext).stories
}

export function useWorkConfig() {
  return useContext(ContentContext).work
}

export function useAboutConfig() {
  return useContext(ContentContext).about
}
